import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FirebaseError } from 'firebase/app';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { motion, MotionConfig, useReducedMotion, type Variants } from 'motion/react';
import { ArrowRight, Building2, Loader2, ShieldCheck } from 'lucide-react';
import { auth } from '@/core/firebase/config';
import { RippleButton } from '@/shared/components/AnimateUi/RippleButton';
import { Badge } from '@/shared/components/ui/badge';
import { Card } from '@/shared/components/ui/card';
import { useAppLogo } from '@/shared/config/branding';
import { cn } from '@/shared/utils/utils';
import useSaveUser from '@/features/user/hooks/useSaveUser';

interface LoginSearch {
  from?: string;
}

export const Route = createFileRoute('/login')({
  component: Login,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    from: search.from as string,
  }),
});

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

const rise: Variants = {
  hidden: { opacity: 0, transform: 'translateY(18px)' },
  show: {
    opacity: 1,
    transform: 'translateY(0px)',
    transition: { duration: 0.6, ease: EASE_OUT },
  },
};

const fade: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.6, ease: EASE_OUT } },
};

/** Closing the Google popup or opening a second one isn't an error worth showing. */
const DISMISSED_POPUP_CODES = ['auth/popup-closed-by-user', 'auth/cancelled-popup-request'];

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
  hd: 'hootsuite.com', // Restrict to hootsuite.com domain
});

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden className={className}>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function Login() {
  const navigate = useNavigate();
  const Logo = useAppLogo();
  const reduceMotion = useReducedMotion();
  const item = reduceMotion ? fade : rise;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleLogin = async () => {
    setPending(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user) {
        // Double check the email domain
        if (!result.user.email?.endsWith('@hootsuite.com')) {
          await auth.signOut();
          setError('Only @hootsuite.com Google accounts can sign in.');
          return;
        }
        // Redirect to the original destination or home
        useSaveUser(
          result.user.uid,
          result.user.email,
          result.user.displayName,
          result.user.photoURL
        );

        navigate({
          to: '/booking',
          search: {
            resourceId: undefined,
            officeId: undefined,
            date: undefined,
            startTime: undefined,
            endTime: undefined,
          },
        });
      }
    } catch (err) {
      if (err instanceof FirebaseError && DISMISSED_POPUP_CODES.includes(err.code)) return;
      console.error('Error signing in with Google:', err);
      setError("We couldn't sign you in with Google. Please try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="bg-mint-500 relative isolate overflow-hidden flex-1 flex items-center justify-center px-4 py-12 sm:py-16">
        <motion.div
          aria-hidden
          className="absolute -top-56 -right-48 -z-10 h-[38rem] w-[38rem] bg-[radial-gradient(closest-side,var(--color-card),transparent)] opacity-80"
          animate={
            reduceMotion
              ? undefined
              : {
                  transform: [
                    'translate(0px, 0px) scale(1)',
                    'translate(-40px, 30px) scale(1.08)',
                    'translate(0px, 0px) scale(1)',
                  ],
                }
          }
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        <motion.div
          aria-hidden
          className="absolute -bottom-64 -left-56 -z-10 h-[36rem] w-[36rem] bg-[radial-gradient(closest-side,var(--color-card),transparent)] opacity-60"
          animate={
            reduceMotion
              ? undefined
              : {
                  transform: [
                    'translate(0px, 0px)',
                    'translate(50px, -24px)',
                    'translate(0px, 0px)',
                  ],
                }
          }
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        <div
          aria-hidden
          className="absolute inset-0 -z-10 text-primary opacity-[0.06] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"
        />

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex w-full max-w-md flex-col items-center gap-8"
        >
          <motion.img variants={item} src={Logo} alt="RobinHoot" className="h-16 sm:h-20 w-auto" />

          <motion.div variants={item} className="w-full">
            <Card className="gap-6 rounded-3xl border border-primary/10 p-7 sm:p-9 shadow-xl shadow-primary/5">
              <div className="space-y-3">
                <Badge
                  variant="secondary"
                  className="bg-primary/10 text-primary px-3 py-1 text-xs font-semibold"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  Hootsuite offices
                </Badge>
                <h1 className="font-serif text-2xl sm:text-3xl font-semibold tracking-tight text-foreground leading-tight">
                  Welcome to RobinHoot
                </h1>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Sign in to book a desk or meeting room and see who&apos;s coming in.
                </p>
              </div>

              <div className="space-y-3">
                <RippleButton
                  size="lg"
                  onClick={handleGoogleLogin}
                  disabled={pending}
                  aria-busy={pending}
                  className="relative overflow-hidden w-full h-12 text-base font-semibold shadow-md transition-shadow hover:shadow-lg hover:shadow-primary/20"
                >
                  {pending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-card">
                      <GoogleMark className="h-4 w-4" />
                    </span>
                  )}
                  {pending ? 'Signing in…' : 'Continue with Google'}
                </RippleButton>

                <p
                  role="alert"
                  className={cn(
                    'rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive',
                    !error && 'sr-only'
                  )}
                >
                  {error}
                </p>

                <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />
                  Use your @hootsuite.com Google Workspace account.
                </p>
              </div>
            </Card>
          </motion.div>

          <motion.div variants={item}>
            <Link
              to="/"
              className="group inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              See what RobinHoot can do
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
